import type { ScenarioSpec } from "./types";
import { officeUnderAttackScenario as OUA } from "./office-under-attack.scenario";

/**
 * Reception-terminal briefing on using AI at work.
 * The film is on the left. The practice is Office Under Attack episode 6
 * (the AI assistant), then the camera returns here for the quiz.
 * Not on the public library until it is published.
 */
const VIDEO = "/cdn/sim/awareness-videos/using-ai-safely/using-ai-safely.mp4";
const FILM_STILL = "/cdn/sim/awareness-videos/using-ai-safely/wide/01-approved.jpg";
const GAME_STILL = "/cdn/sim/awareness-videos/using-ai-safely/wide/14-injection.jpg";

function beat(id: string) {
  const found = OUA.beats.find((item) => item.id === id);
  if (!found) throw new Error(`using-ai-safely missing beat ${id}`);
  return found;
}

function device(id: string) {
  const found = OUA.devices.find((item) => item.id === id);
  if (!found) throw new Error(`using-ai-safely missing device ${id}`);
  return found;
}

export const usingAiSafelyScenario: ScenarioSpec = {
  version: 1,
  id: "using-ai-safely",
  title: "Using AI safely at work",
  summary: {
    goal: "Learn to use an approved AI tool on your own work account, send only what the task needs, and check an answer before you share it.",
    youDo: "Watch a film, then handle the contract request: pick the AI account, decide which files to upload and what to black out, choose how the summary reaches Priya, and answer five questions.",
    duration: "Core 4-7 min, AI exercise adds 6-8 min",
    tags: ["ai_use", "data_protection", "security_policies"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Use an approved AI tool, send only what the task needs, and check the answer before you share it",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: OUA.voiceAttackers,
  scene: OUA.scene,

  devices: [
    {
      id: "ai_terminal",
      kind: "breachbrief",
      config: {
        noticeTag: "AI AT WORK",
        noticeKicker: "SECURITY AWARENESS",
        title: "Using AI safely at work",
        offLabel: "Switch on the welcome terminal",
        voiceId: "onyx",
        progressKey: "using-ai-safely",
        idleImage: FILM_STILL,
        videoFirst: true,
        lines: [
          "Work belongs in an approved AI tool, on your own work account, and only the details the task actually needs.",
          "Watch the film on the left, then use the assistant. The quiz stays on this terminal until you are ready.",
        ],
        hubIntro: "Watch the film, then decide how a contract summary should be handled.",
        hubVideo: {
          title: "Using AI safely",
          blurb: "Which tool to use, what never to paste, and how to check an answer before you send it.",
          src: VIDEO,
          poster: FILM_STILL,
        },
        exercisesKicker: "1 interactive exercise",
        exercisesLabel: "Practise",
        exercises: [
          {
            id: "ai-assistant",
            label: "Use the AI assistant",
            blurb: "A colleague asks you to summarise a contract. Choose the account, what you attach, and how the result is shared.",
            key: "game",
            completionBeatId: "b_cam_back",
            icon: "chapters",
            imageUrl: GAME_STILL,
            actionLabel: "Play",
            doneLabel: "Done",
          },
        ],
        cardsTitle: "Practise",
        cardsIntro: "",
        cards: [],
        finishLabel: "Finish the briefing",
        finishHint: "Continue to the quiz",
        finishEarlyHint: "You can take the quiz now, or watch the film and use the assistant first.",
      },
    },
    device("desktop_home"),
    device("ai_assistant"),
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Using AI safely at work",
      kicker: "Reception",
      guide: {
        title: "Start",
        body: "You start at an unattended reception in the NorthBay office. After a short film you go to your desk, where your colleague Priya sends a Teams message asking you to summarise a contract with ChatGPT for tomorrow's steering committee.",
      },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "ai_terminal",
      guide: {
        title: "Reception terminal",
        body: "Switch on the welcome terminal. The film is on the left. The assistant is beside it. The quiz is the button below.",
      },
      completeOn: { action: "continue" },
    },
    beat("b_cam_ai"),
    beat("b_desktop_chatgpt"),
    beat("b_ai_assistant"),
    beat("b_ai_shared_consequence"),
    beat("b_teach_ai_risky"),
    beat("b_teach_ai_safe"),
    { id: "b_cam_back", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you would do",
      quiz: [
        {
          id: "q_tool",
          title: "Question 1",
          prompt: "You need AI for a piece of company work. Which tool do you use?",
          options: [
            { id: "a", label: "A personal chat account you already have open.", icon: "x" },
            { id: "b", label: "The company's approved assistant, signed in with your own work account.", correct: true, icon: "check" },
            { id: "c", label: "A shared login so the whole team can reopen the chat.", icon: "alert" },
          ],
          rationale: "An approved tool on your own work account is one the company can manage. A personal or shared login is not.",
        },
        {
          id: "q_send",
          title: "Question 2",
          prompt: "A colleague asks you to summarise a contract. What do you put into the assistant?",
          options: [
            { id: "a", label: "The whole original file, so nothing is missed.", icon: "x" },
            { id: "b", label: "Only the clauses the task needs, with personal and financial details removed.", correct: true, icon: "check" },
            { id: "c", label: "A public link to the chat, so they can open it themselves.", icon: "alert" },
          ],
          rationale: "Send the least that answers the request. Names, bank details, and access codes should not ride along, and a public link can be opened by anyone.",
        },
        {
          id: "q_draft",
          title: "Question 3",
          prompt: "The assistant drafts a customer reply that promises a refund your policy does not allow. What do you do?",
          options: [
            { id: "a", label: "Send it. The wording sounds professional.", icon: "x" },
            { id: "b", label: "Check it against company policy and correct it before anyone receives it.", correct: true, icon: "check" },
            { id: "c", label: "Trust it. A confident answer is usually right.", icon: "alert" },
          ],
          rationale: "A fluent draft can still break the rules. You check the answer against policy before it leaves.",
        },
        {
          id: "q_hidden",
          title: "Question 4",
          prompt: "A supplier document contains a hidden line telling the assistant to ignore your instructions. What do you do?",
          options: [
            { id: "a", label: "Follow it. The document came from the supplier.", icon: "x" },
            { id: "b", label: "Treat that line as untrusted. It does not override the task you were given.", correct: true, icon: "check" },
            { id: "c", label: "Paste the whole document in and let the assistant decide.", icon: "alert" },
          ],
          rationale: "Text inside a file can try to steer the assistant. You keep control of the task and do not obey a hidden instruction.",
        },
        {
          id: "q_personal",
          title: "Question 5",
          prompt: "A colleague summarises a company contract in their personal AI account. What is the risk?",
          options: [
            { id: "a", label: "None. A personal account is private, so the company data stays safe.", icon: "x" },
            { id: "b", label: "The chat is outside the company's control. It cannot manage, keep or delete what was sent.", correct: true, icon: "check" },
            { id: "c", label: "The chat is deleted as soon as you close the window.", icon: "alert" },
          ],
          rationale: "A personal AI account is not the company's workspace. What you send there is not managed, audited, or recoverable by the company.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_cam_ai", on: "game" },
    { from: "b_cam_ai", to: "b_desktop_chatgpt" },
    { from: "b_desktop_chatgpt", to: "b_ai_assistant" },
    { from: "b_ai_assistant", to: "b_teach_ai_risky", on: "risky" },
    { from: "b_ai_assistant", to: "b_teach_ai_safe", on: "safe" },
    { from: "b_ai_assistant", to: "b_ai_shared_consequence", on: "shared" },
    { from: "b_ai_shared_consequence", to: "b_teach_ai_risky" },
    { from: "b_teach_ai_risky", to: "b_cam_back" },
    { from: "b_teach_ai_safe", to: "b_cam_back" },
    { from: "b_cam_back", to: "b_hub" },
    { from: "b_hub", to: "b_quiz", on: "finish" },
  ],

  initialBeatId: "b_intro",
};
